/**
 * Motor 7, golf 2 sessie C (Brain grote-ronde/75, keuzes 3, 6, 9 en 10): wat het
 * midden naast de zaak leest en de twee handelingen van de balk die geen
 * verzending zijn. Eigen bestand naast `../api.ts`, zodat het gedeelde
 * API-bestand niet groeit.
 *
 * - `GET /api/werkplek/v6/zaken/{id}/midden`: binnenkomst, open belofte en het
 *   overdrachtsblok (`emails.motor7_overdracht`) met het oordeel van de meelezer
 *   en de beschrijving van genoemde handelingen. Alles is optioneel: een oudere
 *   server of een zaak zonder motor 7 geeft gewoon niets.
 * - `POST .../doorzetten`: naar Wacht (wekker met het concept) of naar een collega.
 * - `POST .../balk-meting`: meetplicht voor Niet goed en Alleen les vastleggen.
 */
import { apiClient } from "@/lib/api";

export interface M7Belofte {
  wat: string;
  /** ISO-moment waarop de belofte uiterlijk gebeurd moet zijn. */
  tot: string;
}

export interface M7MeelezerLater {
  oordeel: "goed" | "terug" | "te_laat" | string;
  ronde?: number;
  opmerkingen?: { zin: string; soort: string; reden: string | null }[];
}

/** De vorm van `emails.motor7_overdracht` (kanalen/mail.py, `overdracht`). */
export interface M7Overdracht {
  beurt_id?: string;
  uitgang?: string | null;
  bij_versturen?: {
    beloftes?: { soort: string; zin: string }[];
    handelingen?: { naam: string; onderbouwing: string | null }[];
  } | null;
  meelezer?: { ontbrak?: boolean; regel?: string | null; later?: M7MeelezerLater | null } | null;
  doorgezet?: { onderwerp: string; voorstel: string; handeling: string | null }[];
  handelingen?: { naam: string; uitslag: string; reden: string | null; kaart_ref: string | null }[];
}

export interface M7Midden {
  binnen: string | null;
  belofte: M7Belofte | null;
  overdracht: M7Overdracht | null;
  /** Oordeel van de meelezer die op tijd was (uit het spoor). */
  meelezer: { oordeel: string; ronde?: number; soorten?: string[] } | null;
  /** Handelingnaam naar beschrijving uit het register. */
  handelingen: Record<string, string>;
}

export interface M7Collega {
  id: string;
  naam: string;
}

export interface M7Doorgezet {
  naar: "wacht" | "collega";
  regel: string;
  wekker_op: string | null;
}

export const middenSleutels = {
  midden: (zaakId: string) => ["v6", "midden", zaakId] as const,
  collegas: ["v6", "collegas"] as const,
};

const PAD = "/api/werkplek/v6";

export const middenApi = {
  async midden(zaakId: string): Promise<M7Midden> {
    const { data } = await apiClient.get<M7Midden>(`${PAD}/zaken/${zaakId}/midden`);
    return data;
  },
  async collegas(): Promise<M7Collega[]> {
    const { data } = await apiClient.get<M7Collega[]>(`${PAD}/collegas`);
    return data;
  },
  async doorzetten(
    zaakId: string,
    vraag: { naar: "wacht" | "collega"; gebruiker_id?: string | null; tekst?: string | null },
  ): Promise<M7Doorgezet> {
    const { data } = await apiClient.post<M7Doorgezet>(`${PAD}/zaken/${zaakId}/doorzetten`, vraag);
    return data;
  },
  /** Fire-and-forget: een mislukte meting zegt nooit iets tegen JP. */
  meetBalk(zaakId: string, knop: "niet_goed" | "alleen_les"): void {
    void apiClient.post(`${PAD}/zaken/${zaakId}/balk-meting`, { knop }).catch(() => undefined);
  },
};
